import { useEffect, useRef, type ReactNode } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { isoDateInZone, partsInZone, type Task } from '@cleartask/shared';
import { useTimeZone } from '../../session.js';
import { TaskChip } from './TaskChip.js';
import { dropTargetId } from './useCalendar.js';

/** Altura de uma hora, em px. Fixa para o posicionamento da linha "agora". */
const HOUR_HEIGHT = 48;

/** Primeira hora visivel ao abrir. Comecar em 00:00 mostraria a madrugada vazia. */
const SCROLL_TO_HOUR = 7;

const HOURS = Array.from({ length: 24 }, (_, h) => h);

const DIA_CURTO = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

interface TimeGridViewProps {
  /** Inicio de cada dia civil exibido. 1, 3 ou 7 colunas. */
  days: number[];
  byDay: Map<string, Task[]>;
  now: number;
  onOpen: (task: Task) => void;
}

/**
 * Grade de horas — atende Dia, 3 Dias e Semana (spec 9.4).
 *
 * As tres visoes da spec sao a mesma coisa com 1, 3 ou 7 colunas. Manter tres
 * componentes separados, como a spec 12 sugere, significaria corrigir cada
 * bug de layout tres vezes.
 *
 * Cada celula (dia x hora) e um alvo de drop proprio. Soltar nela reagenda a
 * tarefa para aquele dia naquela hora.
 */
export function TimeGridView({ days, byDay, now, onOpen }: TimeGridViewProps) {
  const timeZone = useTimeZone();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Abre no horario de trabalho em vez de 00:00. Sem isso, a primeira coisa
  // que a pessoa ve e a madrugada — sempre vazia.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = SCROLL_TO_HOUR * HOUR_HEIGHT;
  }, []);

  const hoje = isoDateInZone(timeZone, now);
  const agora = partsInZone(timeZone, now);
  const colunas = `3rem repeat(${days.length}, minmax(0, 1fr))`;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded bg-surface shadow-card">
      <div className="grid border-b border-border" style={{ gridTemplateColumns: colunas }}>
        <div />
        {days.map((day) => {
          const p = partsInZone(timeZone, day);
          const ehHoje = isoDateInZone(timeZone, day) === hoje;

          return (
            <div key={day} className="px-1 py-2 text-center">
              <div className="text-xs text-text-mut">{DIA_CURTO[p.weekday]}</div>
              <div
                className={[
                  'mx-auto grid h-7 w-7 place-items-center rounded-full text-sm tabular-nums',
                  ehHoje ? 'bg-brand-blue font-semibold text-white' : '',
                ].join(' ')}
              >
                {p.day}
              </div>
            </div>
          );
        })}
      </div>

      {/* Faixa "dia inteiro" (spec 9.4): tarefas sem hora marcada. */}
      <div
        className="grid border-b border-border bg-surface-2/40"
        style={{ gridTemplateColumns: colunas }}
      >
        <div className="px-1 py-1 text-right text-xs text-text-mut">dia</div>
        {days.map((day) => {
          const date = isoDateInZone(timeZone, day);
          const doDia = (byDay.get(date) ?? []).filter((t) => t.allDay);

          return (
            <AllDayCell key={day} date={date}>
              {doDia.map((task) => (
                <TaskChip key={task.id} task={task} now={now} showTime={false} onOpen={onOpen} />
              ))}
            </AllDayCell>
          );
        })}
      </div>

      <div ref={scrollRef} className="relative min-h-0 flex-1 overflow-y-auto">
        <div className="grid" style={{ gridTemplateColumns: colunas }}>
          <div>
            {HOURS.map((hour) => (
              <div
                key={hour}
                className="relative border-b border-border pr-1 text-right text-xs tabular-nums text-text-mut"
                style={{ height: HOUR_HEIGHT }}
              >
                <span className="absolute -top-2 right-1">
                  {String(hour).padStart(2, '0')}
                </span>
              </div>
            ))}
          </div>

          {days.map((day) => {
            const date = isoDateInZone(timeZone, day);
            const doDia = byDay.get(date) ?? [];
            const ehHoje = date === hoje;

            return (
              <div key={day} className="relative border-l border-border">
                {HOURS.map((hour) => (
                  <HourCell
                    key={hour}
                    date={date}
                    hour={hour}
                    tasks={doDia.filter(
                      (t) =>
                        !t.allDay &&
                        t.dueAt !== null &&
                        partsInZone(timeZone, t.dueAt).hour === hour,
                    )}
                    now={now}
                    onOpen={onOpen}
                  />
                ))}

                {ehHoje ? <LinhaAgora hour={agora.hour} minute={agora.minute} /> : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AllDayCell({ date, children }: { date: string; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({
    id: dropTargetId({ kind: 'allday', date }),
  });

  return (
    <div
      ref={setNodeRef}
      className={[
        'flex min-h-8 flex-col gap-1 border-l border-border p-1 transition-colors duration-150',
        isOver ? 'bg-brand-blue-soft' : '',
      ].join(' ')}
    >
      {children}
    </div>
  );
}

interface HourCellProps {
  date: string;
  hour: number;
  tasks: Task[];
  now: number;
  onOpen: (task: Task) => void;
}

function HourCell({ date, hour, tasks, now, onOpen }: HourCellProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: dropTargetId({ kind: 'slot', date, hour }),
  });

  return (
    <div
      ref={setNodeRef}
      className={[
        'flex flex-col gap-0.5 overflow-hidden border-b border-border p-0.5 transition-colors duration-150',
        isOver ? 'bg-brand-blue-soft' : '',
      ].join(' ')}
      style={{ height: HOUR_HEIGHT }}
    >
      {tasks.map((task) => (
        <TaskChip key={task.id} task={task} now={now} onOpen={onOpen} />
      ))}
    </div>
  );
}

/**
 * Linha do "agora" (spec 9.4). Vermelha — a unica outra coisa vermelha na
 * interface e vencido/Q1, e ambas significam a mesma coisa: o tempo passou.
 */
function LinhaAgora({ hour, minute }: { hour: number; minute: number }) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-overdue"
      style={{ top: (hour + minute / 60) * HOUR_HEIGHT }}
    />
  );
}
